/* eslint-disable @next/next/no-img-element */
'use client';

import classNames from 'classnames';
import React from 'react';
import {useViewport, withinOrWiderThan} from '@udemy-v2/react-layout-components';
import {useI18n} from '@udemy-v2/i18n';

import {
    FooterAboutLinks,
    FooterCertificationBySkillLinks,
    FooterCommunicationLinks,
    FooterDataScienceLinks,
    FooterInDemandCareesLinks,
    FooterITCertificationLinks,
    FooterLeadershipLinks,
    FooterBusinessAnalyticsLinks,
    FooterWebDevelopmentLinks,
    LogoSection,
    FooterDiscoverLinks,
    FooterUdemyBusinessLinks,
    FooterLegalLinks,
} from './footer.common';
import {FooterMobile} from './footer.mobile';

import styles from './footer.module.scss';

export const Footer: React.FC = () => {
    const {range} = useViewport();
    const isWideViewport = range && withinOrWiderThan(range, 'xl');
    const FooterSectionComponent = isWideViewport ? FooterSection : FooterMobile;

    return <FooterSectionComponent />;
};

function FooterSection() {
    const {gettext} = useI18n();
    return (
        <footer className={classNames('ud-color-scheme-dark', styles['footer-container'])}>
            <div className={styles['footer-section']}>
                <div className={styles['notice-container']}>
                    <div className={`${styles.notice} ud-heading-lg`}>
                        {gettext('Teach the World Online')}
                        <div className={styles['teach-container-subtext']}>
                            {gettext(
                                'Create an online video course, reach students across the globe, earn money',
                            )}
                        </div>
                    </div>
                    <div className={styles['language-selector']}>
                        <div className={styles['teach-on-udemy-container']}>
                            <a
                                href="/teaching/?ref=teach_footer"
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles['language-selector-label']}
                            >
                                {gettext('Teach on Udemy')}
                            </a>
                        </div>
                        <div className={styles['filler']}></div>
                    </div>
                </div>

                <div className={styles['notice-container']}>
                    <div className={`${styles.notice} ud-heading-lg`}>
                        {gettext('Top companies choose ')}
                        <a
                            data-purpose="ufb-link"
                            className={styles.invertedLink}
                            href="/udemy-business/?locale=en_US&mx_pg=indeed-page&path=%2F&ref=footer"
                            target="_blank"
                            rel="noopener"
                        >
                            {gettext('Udemy Business')}
                        </a>{' '}
                        {gettext('to build in-demand career skills.')}
                    </div>
                    <div className={styles['partner-logos']}>
                        <img
                            className={styles['partner-logo']}
                            src="https://s.udemycdn.com/partner-logos/v4/nasdaq-light.svg"
                            alt="Nasdaq"
                            height="44"
                            width="115"
                            loading="lazy"
                        />
                        <img
                            className={styles['partner-logo']}
                            src="https://s.udemycdn.com/partner-logos/v4/volkswagen-light.svg"
                            alt="Volkswagen"
                            height="44"
                            width="44"
                            loading="lazy"
                        />
                        <img
                            className={styles['partner-logo']}
                            src="https://s.udemycdn.com/partner-logos/v4/netapp-light.svg"
                            alt="NetApp"
                            height="44"
                            width="115"
                            loading="lazy"
                        />
                        <img
                            className={styles['partner-logo']}
                            src="https://s.udemycdn.com/partner-logos/v4/eventbrite-light.svg"
                            alt="Eventbrite"
                            height="44"
                            width="115"
                            loading="lazy"
                        />
                    </div>
                </div>

                <div className={styles['footer-section-group']}>
                    <div className={styles['footer-section-group-column']}>
                        <div className={styles['footer-section-group-column-title']}>
                            {gettext('In-Demand Careers')}
                        </div>
                        <FooterInDemandCareesLinks />
                        <div className={styles['footer-section-group-column-title']}>
                            {gettext('Certification by Skill')}
                        </div>
                        <FooterCertificationBySkillLinks />
                    </div>
                    <div className={styles['footer-section-group-column']}>
                        <div className={styles['footer-section-group-column-title']}>
                            {gettext('Web Development')}
                        </div>
                        <FooterWebDevelopmentLinks />
                        <div className={styles['footer-section-group-column-title']}>
                            {gettext('Data Science')}
                        </div>
                        <FooterDataScienceLinks />
                    </div>
                    <div className={styles['footer-section-group-column']}>
                        <div className={styles['footer-section-group-column-title']}>
                            {gettext('IT Certification')}
                        </div>
                        <FooterITCertificationLinks />
                        <div className={styles['footer-section-group-column-title']}>
                            {gettext('Communication')}
                        </div>
                        <FooterCommunicationLinks />
                    </div>

                    <div className={styles['footer-section-group-column']}>
                        <div className={styles['footer-section-group-column-title']}>
                            {gettext('Leadership')}
                        </div>
                        <FooterLeadershipLinks />
                        <div className={styles['footer-section-group-column-title']}>
                            {gettext('Business Analytics & Intelligence')}
                        </div>
                        <FooterBusinessAnalyticsLinks />
                    </div>
                </div>

                <div className={styles['footer-section-group']}>
                    <div className={styles['footer-section-group-column']}>
                        <div className={styles['footer-section-group-column-title']}>
                            {gettext('About')}
                        </div>
                        <FooterAboutLinks />
                    </div>
                    <div className={styles['footer-section-group-column']}>
                        <div className={styles['footer-section-group-column-title']}>
                            {gettext('Discover Udemy')}
                        </div>
                        <FooterDiscoverLinks />
                    </div>{' '}
                    <div className={styles['footer-section-group-column']}>
                        <div className={styles['footer-section-group-column-title']}>
                            {gettext('Udemy for Business')}
                        </div>
                        <FooterUdemyBusinessLinks />
                    </div>{' '}
                    <div className={styles['footer-section-group-column']}>
                        <div className={styles['footer-section-group-column-title']}>
                            {gettext('Legal & Accessibility')}
                        </div>
                        <FooterLegalLinks />
                    </div>
                </div>
                <LogoSection />
            </div>
        </footer>
    );
}
